/**
 * PII (Personally Identifiable Information) utilities and constants.
 *
 * Shared across the frontend to detect, protect, and manage PII fields
 * that the backend strips/masks based on the requester's contextual access.
 */

// ── Mask character used by the backend ──────────────────────────────────────
export const PII_MASK_CHAR = '•'

// ── Tier 1 – Restricted PII ────────────────────────────────────────────────
// Visible to self + contextually authorized leaders (BaseLeader, GroupLeader, TeamLeader)
export const TIER_1_FIELDS = new Set([
  'gender',
  'vehicleLicensePlateNumber',
  'vehicleLicensePlateState',
  'healthConditions',
  'backgroundCheck',
  'medicalNeeds',
])

// ── Tier 2 – Sensitive PII ──────────────────────────────────────────────────
// Self sees full value; authorized leaders see masked (revealable); others stripped entirely
// DOB is masked to year only (**/**/YYYY); others show last 4 chars (•••••1234)
export const TIER_2_FIELDS = new Set([
  'dateOfBirth',
  'passportNumber',
  'healthInsurancePolicyNumber',
  'travelInsurancePolicyNumber',
])

/** All PII field names (Tier 1 + Tier 2) */
export const ALL_PII_FIELDS = new Set([...TIER_1_FIELDS, ...TIER_2_FIELDS])

/**
 * Check if a string value appears to be a backend-masked PII value.
 * Detects both bullet masks (e.g. "•••••1234") and year-only DOB masks.
 * Used to prevent accidentally saving masked values back to the backend.
 */
export function isMaskedValue(value: unknown): boolean {
  if (typeof value !== 'string') return false
  return value.includes(PII_MASK_CHAR) || /^\*\*\/\*\*\/\d{4}$/.test(value)
}

/**
 * Check if a PII field was stripped (undefined/null) from the API response,
 * indicating the current user doesn't have access to it.
 */
export function isFieldRestricted(value: unknown): boolean {
  return value === undefined
}

/**
 * Strip masked PII values from a payload before submitting to the backend.
 * This prevents accidentally overwriting real values with masked strings.
 *
 * @param payload - The form data object to sanitize
 * @returns A new object with masked fields removed
 */
export function stripMaskedPiiFromPayload<T extends Record<string, unknown>>(
  payload: T,
): T {
  const cleaned = { ...payload }
  for (const key of Object.keys(cleaned)) {
    if (ALL_PII_FIELDS.has(key) && isMaskedValue(cleaned[key])) {
      delete cleaned[key]
    }
  }
  return cleaned
}
